import type { RevenueBasis } from '@servwell/shared';

/**
 * The Revenue page's words for each side of its Invoiced | Collected switch (ticket 07): what
 * its figures and rows are called, and the one sentence that says what the number is. Words
 * only: every figure, and which basis an answer is on, is the server's (`report.revenue`,
 * `report.revenueDrill`). The one place the page, its sections and its drill look a word up
 * by basis.
 */
export interface BasisWords {
  /** The switch's option, the headline, the amount column, the chart: "Invoiced". */
  label: string;
  /** What the number is, in one sentence, above every number on the page (spec story 2). */
  definition: string;
  /** The second headline figure's name. */
  jobs: string;
  /** The by-type table's jobs column: "Jobs", or "Jobs paid for". */
  jobsColumn: string;
  /** The third figure's name, and the by-type table's average column. */
  averageJob: string;
  /** A person's job count: "1 job", "2 jobs paid for". */
  jobCount: (n: number) => string;
  /** The fourth figure's name: the other basis, over the same days. */
  otherBasis: string;
  /** Said under the fourth, when it could be misread (spec story 6); nothing otherwise. */
  otherBasisNote?: string;
  /** The figure as a verb: "Nothing invoiced in …", "$1,400.00 invoiced on 2 jobs". */
  verb: string;
  /** When an empty shop's period fills: "come back once invoices have gone out". */
  fillsOnce: string;
  /** What a figure opens, in the plural: "open its invoices", "these payments and refunds". */
  rows: string;
  /** How many rows a drill holds: "1 invoice", "3 payments and refunds". */
  rowCount: (n: number) => string;
  /** A drill with nothing behind it: "No invoices behind this figure …". */
  noRows: string;
}

const count = (n: number) => n.toLocaleString('en-US');

export const BASIS_WORDS: Record<RevenueBasis, BasisWords> = {
  invoiced: {
    label: 'Invoiced',
    definition:
      'Invoiced is invoice subtotals before tax, dated by the day each invoice was issued, with drafts, voids and late fees left out.',
    jobs: 'Jobs invoiced',
    jobsColumn: 'Jobs',
    averageJob: 'Average job',
    jobCount: (n) => (n === 1 ? '1 job' : `${count(n)} jobs`),
    otherBasis: 'Collected in the same days',
    // Money received in these days for invoices of ANY period, so dividing it by what was
    // invoiced in them measures nothing.
    otherBasisNote: 'not a collection rate',
    verb: 'invoiced',
    fillsOnce: 'once invoices have gone out',
    rows: 'invoices',
    rowCount: (n) => (n === 1 ? '1 invoice' : `${count(n)} invoices`),
    noRows: 'No invoices',
  },
  collected: {
    label: 'Collected',
    definition:
      'Collected is payments received less refunds made, dated by the day each payment arrived or refund was made, with the tax and fees customers paid included.',
    jobs: 'Jobs paid for',
    jobsColumn: 'Jobs paid for',
    averageJob: 'Average per job paid for',
    jobCount: (n) => (n === 1 ? '1 job paid for' : `${count(n)} jobs paid for`),
    otherBasis: 'Invoiced in the same days',
    verb: 'collected',
    fillsOnce: 'once payments have come in',
    rows: 'payments and refunds',
    rowCount: (n) =>
      n === 1 ? '1 payment or refund' : `${count(n)} payments and refunds`,
    noRows: 'No payments or refunds',
  },
};
